/**
 * Issue #827 — My Trades' working reading view.
 *
 * The allowlisted slice of "how the trader was reading their report" that
 * survives a departure and return within this page lifetime. It rides the
 * #824 baseline (`workingViewStore` / `useWorkingView`) alongside
 * `ValueDashboard/readingView.ts` and `PositionTracker/readingView.ts`, and
 * keeps the same boundary: reading controls only.
 *
 * The distinction this route makes that no other does (journey matrix §2b):
 * the CONTROLS are cheap and safe to resume, but the rows behind them are
 * ACCOUNT DATA that must be re-fetched on every visit. Nothing here holds a
 * trade, bucket, P&L figure, fee or rejection record — `trades`, `buckets`,
 * `rebateSummary` and `polyBucketsRetained` keep their existing state and are
 * re-read by `fetchAll` on every mount. A reviewer checking that claim should
 * find this interface has no field that could carry one.
 *
 * Deliberately NOT here, because it already has a canonical owner this layer
 * must not become a second reader/writer of: `tradeViewMode` (card/table),
 * a saved display default in `stores/displaySettings.ts` — the one preference
 * store (#691). It survives navigation ALREADY, and durably, which is why
 * resuming it here would be a second authority rather than a new capability.
 */
import type { WorkingViewDefinition } from '@/stores/workingViewStore';
import { DEFAULT_TRADE_SORT, type TradeSortDir, type TradeSortKey, type TradeSortState } from './tradeSort';
import type { TradeVenueFilter } from './UnifiedTradesTable';

/**
 * The report's time window.
 *
 * Every preset is stored as its ENUM and resolved against the real clock by
 * `rangeStartMs(range, now)` at read time, so relative intent stays relative
 * for free: a report left on "7 Days" and returned to tomorrow shows the last
 * seven days from tomorrow, not the window those words meant on the way out.
 * Only `'custom'` names absolute days — see `customStart` / `customEnd`.
 */
export type TimeRange = 'total' | 'today' | '7d' | '28d' | 'custom';

/** Closed-only rows, or every row including still-open positions. */
export type ViewMode = 'closed' | 'all';

/** The Kalshi stat/table lens. Both values are always available — neither tab
 *  is gated by role or grant on this surface (verified against `TabBar`), so
 *  "an unauthorized tab must not be restored" has nothing to bite on here.
 *  The store's own auth scope still drops the whole slice on any identity,
 *  role or grant change, which is where that guarantee actually lives. */
export type Tab = 'audit' | 'trader';

export interface TradesReading {
  range: TimeRange;
  /** Absolute `yyyy-mm-dd` bounds, or `''` for "not set". These are the only
   *  fields here that name a fixed day, and they stay exactly the days the
   *  trader typed — a custom interval is an explicit choice, not an intent. */
  customStart: string;
  customEnd: string;
  view: ViewMode;
  tab: Tab;
  showRejected: boolean;
  sort: TradeSortState;
  venueFilter: TradeVenueFilter;
}

const TIME_RANGES: TimeRange[] = ['total', 'today', '7d', '28d', 'custom'];
const VIEW_MODES: ViewMode[] = ['closed', 'all'];
const TABS: Tab[] = ['audit', 'trader'];
// #1031 adds 'polymarket_us'; a saved view from before it still parses.
const VENUE_FILTERS: TradeVenueFilter[] = ['all', 'kalshi', 'polymarket', 'polymarket_us'];
const SORT_KEYS: TradeSortKey[] = ['date', 'pnl'];
const SORT_DIRS: TradeSortDir[] = ['asc', 'desc'];

/** The same shape `parseLocalDateMs` accepts, deliberately duplicated as a
 *  literal rather than shared: this is the STORAGE contract, and it must not
 *  drift silently if the input parser is ever loosened. */
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Explicitly empty bounds retain the existing open-ended report semantics.
 * Malformed bounds must not silently become empty (and widen the report).
 * Incomplete edits live only in the mounted date draft, outside this parser. */
export function validTradesDateRange(start: unknown, end: unknown): boolean {
  const validDay = (value: unknown): value is string => {
    if (value === '') return true;
    if (typeof value !== 'string' || !ISO_DAY.test(value)) return false;
    const [year, month, day] = value.split('-').map(Number);
    if (year < 100) return false;
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day;
  };
  return validDay(start) && validDay(end) && (!start || !end || start <= end);
}

/** Constructed field by field, never spread: an unknown key in stored input
 *  must not reach a consumer, and a `sort` object must not arrive carrying
 *  anything the comparator does not read. */
function readSort(value: unknown): TradeSortState | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<TradeSortState>;
  if (!SORT_KEYS.includes(v.key as TradeSortKey)) return null;
  if (!SORT_DIRS.includes(v.dir as TradeSortDir)) return null;
  return { key: v.key!, dir: v.dir! };
}

export const tradesReading: WorkingViewDefinition<TradesReading> = {
  id: 'mytrades-reading-v1',
  parse(input) {
    if (!input || typeof input !== 'object') return null;
    const v = input as Partial<TradesReading>;
    const sort = readSort(v.sort);
    if (sort === null
      || typeof v.showRejected !== 'boolean'
      || !TIME_RANGES.includes(v.range as TimeRange)
      || !VIEW_MODES.includes(v.view as ViewMode)
      || !TABS.includes(v.tab as Tab)
      || !VENUE_FILTERS.includes(v.venueFilter as TradeVenueFilter)) return null;
    if (!validTradesDateRange(v.customStart, v.customEnd)) return null;
    return {
      range: v.range!,
      customStart: v.customStart!,
      customEnd: v.customEnd!,
      view: v.view!,
      tab: v.tab!,
      showRejected: v.showRejected,
      sort,
      venueFilter: v.venueFilter!,
    };
  },
};

/** The page's existing safe defaults, unchanged — the values the eight
 *  `useState` calls carried before this slice replaced them. */
export const TRADES_READING_DEFAULTS: TradesReading = {
  range: '7d',
  customStart: '',
  customEnd: '',
  view: 'closed',
  tab: 'trader',
  showRejected: false,
  sort: DEFAULT_TRADE_SORT,
  venueFilter: 'all',
};

/** Exactly what `WorkingViewReset` promises to put back, for its tooltip. */
export const TRADES_RESET_SCOPE =
  'date range and custom dates, Closed/All, the Audit/Trader tab, the venue '
  + 'filter, show-rejected, and the table sort';
